import { TrendingUp } from "lucide-react";
import { fmtDate, fmtINR, fmtRate } from "@/lib/format";
import { useApiGet } from "@/lib/useApiGet";
import { SectionHead } from "./MetricTable";

const SCEN = [["worst", "Worst"], ["expected", "Expected"], ["best", "Best"]];

const Input = ({ label, value, testid }) => (
  <div className="flex justify-between gap-3 border-b border-slate-800/70 py-1.5 text-sm last:border-0">
    <span className="text-slate-400">{label}</span>
    <span data-testid={testid} className="font-mono font-semibold text-slate-50">{value}</span>
  </div>
);

const Scenario = ({ name, label, s }) => (
  <div data-testid={`projection-${name}`} className={`rounded-xl border p-4 ${name === "expected" ? "border-indigo-500/50 bg-indigo-500/10" : "border-slate-800 bg-slate-900"}`}>
    <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{label} case</p>
    {s ? (
      <>
        <p className="mt-2 text-xs text-slate-400">Revenue</p>
        <p data-testid={`projection-${name}-revenue`} className="font-mono text-lg font-semibold">{fmtINR(s.revenue)}</p>
        <p className="mt-1 text-xs text-slate-400">Cash</p>
        <p data-testid={`projection-${name}-cash`} className="font-mono text-lg font-semibold">{fmtINR(s.cash)}</p>
        <p className="mt-1 text-xs text-slate-400">Goal</p>
        <p data-testid={`projection-${name}-goal-pct`} className="font-mono font-semibold text-indigo-300">{s.goal_pct === null ? "No goal set" : fmtRate(s.goal_pct)}</p>
      </>
    ) : (
      <p data-testid={`projection-${name}-not-enough`} className="mt-3 text-sm font-medium text-slate-400">Not enough data</p>
    )}
  </div>
);

export const ProjectionSection = ({ deps = [] }) => {
  const { data, error } = useApiGet("/projection", null, deps);
  if (error) return <p className="text-sm text-slate-400" data-testid="projection-error">{error}</p>;
  if (!data) return <p className="text-sm text-slate-400" data-testid="projection-loading">Loading projection…</p>;
  const i = data.inputs;
  return (
    <section data-testid="projection-section">
      <SectionHead title="Month-end projection" sub={`Team-wide forecast to ${fmtDate(data.month_end)} from all-time rates. Best/Worst = rates ±20%, capped at 100%.`} />
      {!data.enough_data && (
        <p data-testid="projection-not-enough-data" className="mb-3 flex items-center gap-2 rounded-xl border border-dashed border-slate-700 bg-slate-900 px-4 py-3 text-sm text-slate-300">
          <TrendingUp className="h-4 w-4" /> Not enough data to derive every rate yet. Forecasts appear once there are taken calls, offers and won deals.
        </p>
      )}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-4">
        <div className="rounded-xl border border-slate-800 bg-slate-900 p-4" data-testid="projection-inputs">
          <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400">Inputs</p>
          <Input label="Meetings remaining" value={i.meetings_remaining} testid="projection-input-meetings-remaining" />
          <Input label="Show rate" value={fmtRate(i.show_rate_pct)} testid="projection-input-show-rate" />
          <Input label="Offer rate" value={fmtRate(i.offer_rate_pct)} testid="projection-input-offer-rate" />
          <Input label="Close on offers" value={fmtRate(i.close_on_offers_pct)} testid="projection-input-close-on-offers" />
          <Input label="Avg deal size" value={fmtINR(i.avg_deal_size)} testid="projection-input-avg-deal" />
          <Input label="Cash ratio" value={fmtRate(i.cash_ratio_pct)} testid="projection-input-cash-ratio" />
          <Input label="Cash so far this month" value={fmtINR(i.month_to_date_cash)} testid="projection-input-mtd-cash" />
          <Input label="Revenue goal" value={data.goal_amount ? fmtINR(data.goal_amount) : "Not set"} testid="projection-input-goal" />
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 lg:col-span-3">
          {SCEN.map(([k, label]) => <Scenario key={k} name={k} label={label} s={data.scenarios[k]} />)}
        </div>
      </div>
    </section>
  );
};
